import { memo, useMemo } from "react";
import { polarToCartesian, describeArc } from "../utils/polar";

export const CX = 450;
export const CY = 190;
export const ARC_START = 150;
export const ARC_END = 30;
export const ARC_SWEEP = 240;

const R_BG = 170;
const SW_BG = 22;
const R_THIN = 178;
const SW_THIN = 8;
const R_THICK = 163;
const SW_THICK = 20;
const R_HAIRLINE = 186;
const R_SCALE_RING = 148;
const R_LABELS = 133;
const LABEL_COUNT = 11;
const DANGER_GAP = 2.5;
const PRE_RED_GAP = 4;

// Gradient endpoints — all inputs are module-level constants, computed once
const GRAD_X1 = polarToCartesian(CX, CY, R_THIN, ARC_START).x;
const GRAD_Y1 = polarToCartesian(CX, CY, R_THIN, ARC_START).y;
const GRAD_X2 = polarToCartesian(CX, CY, R_THIN, ARC_START + ARC_SWEEP / 2).x;
const GRAD_Y2 = polarToCartesian(CX, CY, R_THIN, ARC_START + ARC_SWEEP / 2).y;

// Scale label positions — fully static, computed once
const SCALE_LABELS = Array.from({ length: LABEL_COUNT }, (_, i) => {
  const angleDeg = ARC_START + (i / (LABEL_COUNT - 1)) * ARC_SWEEP;
  const pt = polarToCartesian(CX, CY, R_LABELS, angleDeg);
  return { x: pt.x, y: pt.y, label: i };
});

// Static arc paths that never depend on props
const HAIRLINE_PATH = describeArc(CX, CY, R_HAIRLINE, ARC_START, ARC_END);
const SCALE_RING_PATH = describeArc(CX, CY, R_SCALE_RING, ARC_START, ARC_END);

type Props = {
  acceleration: number;
  dangerThresholdPct: number;
};

export const MainArc = memo(function MainArc({ acceleration, dangerThresholdPct }: Props) {
  // Danger zone geometry depends only on dangerThresholdPct — effectively constant per vehicle
  const danger = useMemo(() => {
    const dangerStartAngle = ARC_START + dangerThresholdPct * ARC_SWEEP;
    const dangerSpan = (ARC_END - dangerStartAngle + 360) % 360;
    const segSpan = (dangerSpan - DANGER_GAP) / 2;
    const dangerSeg1End = dangerStartAngle + segSpan;
    const dangerSeg2Start = dangerSeg1End + DANGER_GAP;
    const grayTrackEnd = dangerStartAngle - PRE_RED_GAP;
    return {
      dangerStartAngle,
      dangerSeg1End,
      dangerSeg2Start,
      grayTrackEnd,
      bgPath: describeArc(CX, CY, R_BG, ARC_START, grayTrackEnd),
      d1ThickPath: describeArc(CX, CY, R_THICK, dangerStartAngle, dangerSeg1End),
      d1ThinPath: describeArc(CX, CY, R_THIN, dangerStartAngle, dangerSeg1End),
      d2ThickPath: describeArc(CX, CY, R_THICK, dangerSeg2Start, ARC_END),
      d2ThinPath: describeArc(CX, CY, R_THIN, dangerSeg2Start, ARC_END),
    };
  }, [dangerThresholdPct]);

  const rpmFrac = Math.min(Math.max(acceleration, 0), 1);
  const rpmEndAngle = ARC_START + rpmFrac * ARC_SWEEP;
  const blueEndAngle = Math.min(rpmEndAngle, danger.grayTrackEnd);
  const hasGray = rpmEndAngle < danger.grayTrackEnd;

  return (
    <>
      <defs>
        <linearGradient
          id="blueGrad"
          gradientUnits="userSpaceOnUse"
          x1={GRAD_X1}
          y1={GRAD_Y1}
          x2={GRAD_X2}
          y2={GRAD_Y2}
        >
          <stop offset="0%" stopColor="#354B99" />
          <stop offset="60%" stopColor="#4968d5" />
          <stop offset="100%" stopColor="#587CFF" />
        </linearGradient>
        <filter id="blueBloom" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="8" />
        </filter>
        <filter id="redGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id="needleGlow" x="-80%" y="-20%" width="260%" height="140%">
          <feGaussianBlur stdDeviation="4" />
        </filter>
      </defs>

      {/* Hairline */}
      <path
        d={HAIRLINE_PATH}
        fill="none"
        stroke="#ffffff"
        strokeWidth="0.75"
        strokeOpacity="0.07"
        strokeLinecap="butt"
      />

      {/* Background track */}
      <path
        d={danger.bgPath}
        fill="none"
        stroke="#1e2028"
        strokeWidth={SW_BG}
        strokeLinecap="butt"
      />

      {/* Red danger — segment 1 */}
      <path
        d={danger.d1ThickPath}
        fill="none"
        stroke="#4A1F1F"
        strokeWidth={SW_THICK}
        strokeOpacity="0.65"
        strokeLinecap="butt"
      />
      <path
        d={danger.d1ThinPath}
        fill="none"
        stroke="#c01e1e"
        strokeWidth={SW_THIN}
        strokeLinecap="butt"
      />
      <path
        d={danger.d1ThinPath}
        fill="none"
        stroke="#ff2020"
        strokeWidth={SW_THIN + 6}
        strokeOpacity="0.35"
        strokeLinecap="butt"
        filter="url(#redGlow)"
      />
      {/* Red danger — segment 2 */}
      <path
        d={danger.d2ThickPath}
        fill="none"
        stroke="#4A1F1F"
        strokeWidth={SW_THICK}
        strokeOpacity="0.65"
        strokeLinecap="butt"
      />
      <path
        d={danger.d2ThinPath}
        fill="none"
        stroke="#c01e1e"
        strokeWidth={SW_THIN}
        strokeLinecap="butt"
      />
      <path
        d={danger.d2ThinPath}
        fill="none"
        stroke="#ff2020"
        strokeWidth={SW_THIN + 6}
        strokeOpacity="0.35"
        strokeLinecap="butt"
        filter="url(#redGlow)"
      />

      {/* Gray inactive segment */}
      {hasGray && (
        <>
          <path
            d={describeArc(CX, CY, R_THICK, blueEndAngle, danger.grayTrackEnd)}
            fill="none"
            stroke="#333333"
            strokeWidth={SW_THICK}
            strokeOpacity="0.9"
            strokeLinecap="butt"
          />
          <path
            d={describeArc(CX, CY, R_THIN, blueEndAngle, danger.grayTrackEnd)}
            fill="none"
            stroke="#5d5d5d"
            strokeWidth={SW_THIN}
            strokeLinecap="butt"
          />
        </>
      )}

      {/* Blue RPM fill — follows acceleration, same angle as needle */}
      {rpmFrac > 0 && (
        <>
          {/* Bloom */}
          <path
            d={describeArc(CX, CY, R_THIN, ARC_START, blueEndAngle)}
            fill="none"
            stroke="#587CFF"
            strokeWidth={SW_THIN + 10}
            strokeOpacity="0.35"
            strokeLinecap="butt"
            filter="url(#blueBloom)"
          />
          {/* Inner thick soft blue */}
          <path
            d={describeArc(CX, CY, R_THICK, ARC_START, blueEndAngle)}
            fill="none"
            stroke="rgba(88, 124, 255, 0.20)"
            strokeWidth={SW_THICK}
            strokeOpacity="0.6"
            strokeLinecap="butt"
          />
          {/* Outer thin vivid blue */}
          <path
            d={describeArc(CX, CY, R_THIN, ARC_START, blueEndAngle)}
            fill="none"
            stroke="url(#blueGrad)"
            strokeWidth={SW_THIN}
            strokeLinecap="butt"
          />
        </>
      )}

      {/* Scale ring */}
      <path
        d={SCALE_RING_PATH}
        fill="none"
        stroke="rgba(42, 46, 58, 0.55)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />

      {/* Numeric scale */}
      {SCALE_LABELS.map(({ x, y, label }) => (
        <text
          key={label}
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="central"
          fill="rgba(255,255,255,0.5)"
          fontSize="18"
          fontWeight="600"
          className="purista-font"
        >
          {label}
        </text>
      ))}

      {/* Needle — follows acceleration (RPM), CSS-transitioned for smooth gear-shift animation */}
      <g
        style={{
          transform: `rotate(${rpmEndAngle}deg)`,
          transformOrigin: `${CX}px ${CY}px`,
          transition: "transform 0.15s ease-out",
          willChange: "transform",
        }}
      >
        <line
          x1={CX + R_LABELS - 8}
          y1={CY}
          x2={CX + R_HAIRLINE - 2}
          y2={CY}
          stroke="#ff1010"
          strokeWidth="6"
          strokeLinecap="round"
          strokeOpacity="0.5"
          filter="url(#needleGlow)"
        />
        <line
          x1={CX + R_LABELS - 8}
          y1={CY}
          x2={CX + R_HAIRLINE - 2}
          y2={CY}
          stroke="#ff2828"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </g>
    </>
  );
});
